import { Combobox } from '@/components/ui/combobox/combobox';
import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import { dealStore } from '@/models/stores/deals';
import type { Deal, RapidReconStep } from '@/models/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { noop } from 'lodash';
import { useMemo } from 'react';
import { RAPID_RECON_STEP_OPTIONS_MAP } from './rapid-recon-step-selector';

type RapidReconStepCombobox = {
  id: Deal['id'];
  shortId: Deal['short_id'];
  value?: RapidReconStep;
  afterOnChange?: () => void;
  onSuccess?: (
    data: Deal,
    variables: { rapidReconStep: RapidReconStep },
  ) => void;
  onError?: (
    error: Error,
    variables: { rapidReconStep: RapidReconStep },
  ) => void;
};

export function RapidReconStepCombobox({
  id,
  shortId,
  value,
  afterOnChange,
  onSuccess,
  onError,
}: RapidReconStepCombobox) {
  const queryClient = useQueryClient();

  const updateStatusMutation = useMutation({
    onMutate: async ({ rapidReconStep }) => {
      dealStore.updateRapidReconStep({
        id,
        rapidReconStep,
      });
    },
    mutationFn: async ({
      rapidReconStep,
    }: {
      rapidReconStep: RapidReconStep;
    }) => {
      const response = await pdkAxios
        .post(`/deals/${id}/rapid-recon-step`, {
          rapid_recon_step: rapidReconStep,
        })
        .catch((e) => {
          console.error('Error updating rapid recon step', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: (data, variables) => {
      onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      onError?.(error, variables);

      console.error('Error updating rapid recon step', error, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', shortId] });
    },
  });

  const handleRapidReconStepChange = (newRapidReconStep: RapidReconStep) => {
    updateStatusMutation.mutate({
      rapidReconStep: newRapidReconStep,
    });

    afterOnChange?.();
  };

  const currentOption = useMemo(() => {
    return RAPID_RECON_STEP_OPTIONS_MAP.find(
      (option) => option.value === value,
    );
  }, [value]);

  return (
    <Combobox
      endAdornment={
        <KeyboardKeyIcon>{HOTKEYS.DEAL_RAPID_RECON_STEP}</KeyboardKeyIcon>
      }
      placeholder="Set rapid recon step..."
      emptyMessage="No rapid recon step found"
      currentOption={currentOption}
      options={RAPID_RECON_STEP_OPTIONS_MAP}
      onKeyDown={(e) => {
        const keyPressed = e.key;

        const option = RAPID_RECON_STEP_OPTIONS_MAP.find(
          (option) => option.hotkey === keyPressed,
        );

        if (option) {
          handleRapidReconStepChange(option.value);
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      onChange={handleRapidReconStepChange}
      setOpen={noop}
    ></Combobox>
  );
}
